// Librairies
import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

// Components
import { TooltipComponent } from 'projects/ui-library/src/lib/components/tooltip/tooltip.component';
import { WidgetLoadingComponent } from 'src/app/components/project-pages/dashboard/widget-loading/widget-loading.component';
import { WidgetTitleComponent } from 'src/app/components/project-pages/dashboard/widget-title/widget-title.component';
import { WidgetDirective } from 'src/app/components/project-pages/dashboard/widgets/widget.directive';

// Settings, in widget.design.heatmap.
export interface HeatmapDesign {
  scale?: 'quantile' | 'fixed';     // default quantile
  medium?: number | null;           // fixed: from this value, medium
  high?: number | null;             // fixed: from this value, high
  color?: string;                   // the scale color, the theme's by default
}

export interface HeatmapColumn {
  key: number;                      // the value of x[binning] from the API
  label: string;
}

export interface HeatmapRow {
  label: string;
  values: number[];                 // one per column, 0 when missing
}

export interface HeatmapPeak {
  row: string;
  column: string;
  value: number;
}

// 0 = none, 1..3 = low, medium, high.
export type HeatmapLevel = 0 | 1 | 2 | 3;

// Columns of a periodic date binning, as the API groups them: hour 0..23, day 7..13 ($dayOfWeek + 6, 7 = Sunday), month 0..11.
export function heatmapColumns(binning: string, locale: string): HeatmapColumn[] {
  if (binning === 'day') {
    const weekday = new Intl.DateTimeFormat(locale, { weekday: 'short' });
    // Monday first. January 2024: the 7th is a Sunday, so day key k falls on the k-th.
    return [8, 9, 10, 11, 12, 13, 7].map(key => ({ key, label: weekday.format(new Date(2024, 0, key)) }));
  }

  if (binning === 'month') {
    const month = new Intl.DateTimeFormat(locale, { month: 'short' });
    return Array.from({ length: 12 }, (_unused, key) => ({ key, label: month.format(new Date(2024, key, 1)) }));
  }

  return Array.from({ length: 24 }, (_unused, key) => ({ key, label: `${key.toString().padStart(2, '0')}h` }));
}

// One row per project (synthesis), per series (project dashboard), or a single one; the missing cells at 0.
// The busiest row first.
export function buildHeatmap(rows: any[], binning: string, columns: HeatmapColumn[]): HeatmapRow[] {
  const index = new Map(columns.map((column, position) => [column.key, position]));
  const matrix = new Map<string, number[]>();

  for (const row of rows ?? []) {
    const key = typeof row.x === 'object' && row.x !== null ? row.x[binning] : row.x;
    const position = index.get(Number(key));
    if (position === undefined) continue;

    const label = `${row.p ?? row.s ?? ''}`;
    const values = matrix.get(label) ?? matrix.set(label, columns.map(() => 0)).get(label)!;
    values[position] = (values[position] ?? 0) + (Number(row.y) || 0);
  }

  const total = (values: number[]) => values.reduce((sum, value) => sum + value, 0);
  return [...matrix].map(([label, values]) => ({ label, values })).sort((a, b) => total(b.values) - total(a.values));
}

// Level of a value: by the terciles of the non-zero values, or by the 2 fixed thresholds.
export function heatmapLevels(values: number[], design: HeatmapDesign): (value: number) => HeatmapLevel {
  let medium: number;
  let high: number;

  if (design.scale === 'fixed' && design.medium != null && design.high != null) {
    medium = Number(design.medium);
    high = Number(design.high);
  } else {
    const sorted = values.filter(value => value > 0).sort((a, b) => a - b);
    const at = (ratio: number) => sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * ratio))] ?? 0;
    medium = at(1 / 3);
    high = at(2 / 3);
  }

  return (value: number) => {
    if (value <= 0) return 0;
    if (value >= high) return 3;
    return value >= medium ? 2 : 1;
  };
}

export function heatmapPeak(rows: HeatmapRow[], columns: HeatmapColumn[]): HeatmapPeak | null {
  let peak: HeatmapPeak | null = null;

  rows.forEach(row => row.values.forEach((value, position) => {
    if (value > 0 && (!peak || value > peak.value)) peak = { row: row.label, column: columns[position]!.label, value };
  }));

  return peak;
}

// Heatmap: a periodic date (hours, weekdays or months) in columns, projects or series in rows, intensity in color.
@Component({
  selector: 'app-heatmap',
  standalone: true,
  imports: [
    WidgetLoadingComponent,
    TooltipComponent,
    WidgetTitleComponent,
    CommonModule,
    TranslateModule
  ],
  templateUrl: './heatmap.component.html',
  styleUrl: './heatmap.component.scss'
})

export class HeatmapComponent extends WidgetDirective {
  // Services
  private readonly translate: TranslateService = inject(TranslateService);

  // Variables
  columns: HeatmapColumn[] = [];
  rows: HeatmapRow[] = [];
  peak: HeatmapPeak | null = null;
  scaleColor: string = '';
  titleStyle: string = '';
  axisStep: number = 1;
  hovered: { row: string; column: string; value: number; x: number; y: number } | null = null;
  private levelOf: (value: number) => HeatmapLevel = () => 0;

  init(): void {
    if (!this.widget || !this.widget.visible || this.resize) return;

    const design = this.widget.design ?? {};
    const field = Object.values(this.widget.query?.field ?? {})[0] as any;
    const binning = field?.options?.binning ?? 'hour';

    this.showTitle = design.title?.display ?? true;
    this.titleStyle = `font-size:${design.title?.fontSize || 16}px;text-align:${design.title?.align || 'center'};`;
    this.columns = heatmapColumns(binning, this.translate.currentLang || 'fr-FR');
    this.axisStep = this.columns.length > 12 ? 3 : 1;
    this.rows = buildHeatmap(this.widget.data ?? [], binning, this.columns);
    this.levelOf = heatmapLevels(this.rows.flatMap(row => row.values), design.heatmap ?? {});
    this.peak = heatmapPeak(this.rows, this.columns);
    this.scaleColor = design.heatmap?.color ?? '';
    this.widgetRendered = true;
    this.cdr.detectChanges();
  }

  level(value: number): HeatmapLevel {
    return this.levelOf(value);
  }

  isPeak(value: number): boolean {
    return !!this.peak && value === this.peak.value;
  }

  // Above the hovered cell.
  hover(row: HeatmapRow, column: HeatmapColumn, value: number, event: MouseEvent): void {
    const cell = (event.target as HTMLElement).getBoundingClientRect();
    this.hovered = { row: row.label, column: column.label, value, x: cell.left + cell.width / 2, y: cell.top };
  }
}
